// pages/TaskTrackingPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Divider,
  Alert,
  Snackbar,
  Fab,
  CircularProgress,
  Badge,
  SelectChangeEvent,
  CardMedia,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  LocalShipping as ShippingIcon,
  Phone as PhoneIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Update as UpdateIcon,
  Label as LabelIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Assignment as TaskIcon,
  PriorityHigh as PriorityIcon,
  History as HistoryIcon,
  ViewList as ListIcon,
  ViewModule as GridIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Download as DownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
  OpenInNew as OpenInNewIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios, { AxiosError } from 'axios';
import { io, Socket } from 'socket.io-client';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TaskDetailDrawer, { Task, Employee } from '../components/tasks/TaskDetailDrawer';
import TaskBoardView from '../components/tasks/TaskBoardView';
import {
  RECUR_UNITS,
  clampInterval,
  composeRule,
  recurrenceLabel,
  ruleToControl,
  RecurUnit,
} from '../components/tasks/recurrence';

// Type definitions
// Task and Employee are shared with the detail drawer (single source of truth).

interface TaskHistory {
  id: number;
  task_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  action: string;
  changes?: any;
  created_at: string;
}

interface FormData {
  title: string;
  description: string;
  platform: string;
  assignees: number[]; // 指派給 — several people per task; [] = 未指派
  tags: string[];
  priority: 'low' | 'medium' | 'high';
  due_date: Date | null;
  board_section: string; // 'todo' | 'doing' | 'waiting' (default 'todo')
}

interface TaskStats {
  total: number;
  pending: number;
  completed: number;
  highPriority: number;
}


// Platform is OPTIONAL. The filter offers these two concrete values (a task with
// NULL platform matches only 全部); the create/edit dialog additionally offers
// 不指定 (empty → backend stores NULL). Legacy values are being scrubbed from the DB.
const PLATFORM_OPTIONS: string[] = ['官網', '蝦皮'];
// Active pull-based board columns ('todo' | 'doing' | 'waiting'). 已完成 is
// derived from status, not a section. Used to preselect the create dialog section.
const BOARD_SECTIONS: Array<{ key: string; label: string }> = [
  { key: 'todo', label: '待辦' },
  { key: 'doing', label: '進行中' },
  { key: 'waiting', label: '等待中' },
  { key: 'review', label: '待審核' },
];
const priorityRank: Record<string, number> = { high: 0, medium: 1, low: 2 };
const commonTags: string[] = ['補寄', '聯繫', '取消訂單', '問物流', '退貨', '退款', '地址問題', '缺貨', '換貨', '客訴'];
const priorities: Array<'low' | 'medium' | 'high'> = ['low', 'medium', 'high'];
const priorityColors: Record<string, 'success' | 'warning' | 'error'> = { 
  low: 'success', 
  medium: 'warning', 
  high: 'error' 
};
const priorityLabels: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高'
};

// Task Board v3: two main views (Asana-style board + 我的任務 list) + source labels
type MainView = 'board' | 'my';
const sourceLabels: Record<string, string> = {
  'manual': '手動',
  'auto:pickup': '自動：未取件',
  'auto:care10': '自動：關懷',
  'auto:review': '自動：評價',
  'auto:cycle': '自動：盤點',
  'recur': '週期',
};
const sourceLabel = (source?: string | null): string => {
  if (!source) return '手動';
  return sourceLabels[source] || (source.startsWith('auto') ? '自動' : source);
};

const TaskTrackingPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [mainView, setMainView] = useState<MainView>('board'); // 看板 by default
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterPlatform, setFilterPlatform] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  // Detail drawer (Asana-style): which task is open in the right-side panel.
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);
  // Recurrence control — available on create AND edit. Just an interval and a
  // unit (每 N 天 / 每 N 週), any N; '' unit = 不重複.
  const [recurEveryN, setRecurEveryN] = useState<number>(1); // 1..365d / 1..52w
  const [recurEveryUnit, setRecurEveryUnit] = useState<RecurUnit>('');
  const [imageViewerOpen, setImageViewerOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedTaskHistory, setSelectedTaskHistory] = useState<TaskHistory[] | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    platform: '',
    assignees: [],
    tags: [],
    priority: 'medium',
    due_date: null,
    board_section: 'todo'
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [localActionIds, setLocalActionIds] = useState<Set<number>>(new Set());

  

  // File handling functions
  const handleFileSelect = (files: FileList | null): void => {
    if (!files) return;
    
    const newFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('只能上傳圖片檔案', { variant: 'warning' });
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('圖片大小不能超過 5MB', { variant: 'warning' });
        return false;
      }
      return true;
    });

    // Limit to 5 images total
    const totalFiles = selectedFiles.length + newFiles.length;
    if (totalFiles > 5) {
      enqueueSnackbar('最多只能上傳 5 張圖片', { variant: 'warning' });
      return;
    }

    // Create preview URLs
    const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
  };

  const removeSelectedFile = (index: number): void => {
    // Revoke the object URL to prevent memory leaks
    URL.revokeObjectURL(previewUrls[index]);
    
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removeTaskImage = async (taskId: number, imageIndex: number): Promise<void> => {
  try {
    await api.delete(`/api/tasks/${taskId}/images/${imageIndex}`);
    
    // Update local state
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          images: task.images?.filter((_, index) => index !== imageIndex) || []
        };
      }
      return task;
    }));
    
    enqueueSnackbar('圖片已刪除', { variant: 'success' });
  } catch (error) {
    console.error('Error deleting image:', error);
    enqueueSnackbar('刪除圖片失敗', { variant: 'error' });
  }
};

const openImageViewer = (base64Image: string): void => {
  setSelectedImage(base64Image);
  setImageViewerOpen(true);
};

 

  // Fetch all tasks; the two views (看板 / 我的任務) filter client-side.
  const fetchTasks = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await api.get<Task[]>('/api/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      enqueueSnackbar('無法載入任務列表', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch employees for assignment
  const fetchEmployees = async (): Promise<void> => {
    try {
      const response = await api.get<any[]>('/api/auth/users');
      setEmployees(response.data.map(user => ({
        id: user.id,
        name: user.name || user.email,
        email: user.email
      })));
    } catch (error) {
      console.error('Error fetching employees:', error);
      // If the users endpoint doesn't exist, use mock data
      setEmployees([
        { id: 1, name: '張小明' },
        { id: 2, name: '李小華' },
        { id: 3, name: '王大明' }
      ]);
    }
  };

  // Fetch task history
  const fetchTaskHistory = async (taskId: number): Promise<void> => {
    try {
     const response = await api.get<TaskHistory[]>(`/api/tasks/${taskId}/history`);
      setSelectedTaskHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching task history:', error);
      enqueueSnackbar('無法載入任務歷史', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchEmployees();

    // Set up Socket.IO connection
    const newSocket = io('https://order-management-system-production.up.railway.app', {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    
    setSocket(newSocket);
    
    // Socket event listeners
    newSocket.on('connect', () => {
      console.log('Connected to socket server');
    });
    
    newSocket.on('task-created', (task: Task) => {
      // Check if this task was created locally (to prevent duplicates)
      setLocalActionIds(prev => {
        if (prev.has(task.id)) {
          // This was our own action, remove from tracking and don't add duplicate
          const newSet = new Set(prev);
          newSet.delete(task.id);
          return newSet;
        }
        // This is from another user, add it and show notification
        setTasks(prevTasks => {
          // Double-check it doesn't already exist
          const exists = prevTasks.some(t => t.id === task.id);
          if (exists) return prevTasks;
          return [task, ...prevTasks];
        });
        enqueueSnackbar('新任務已建立', { variant: 'info' });
        return prev;
      });
    });
    
    newSocket.on('task-updated', (updatedTask: Task) => {
      // Check if this was a local update
      setLocalActionIds(prev => {
        if (prev.has(updatedTask.id)) {
          // This was our own action, remove from tracking
          const newSet = new Set(prev);
          newSet.delete(updatedTask.id);
          return newSet;
        }
        // This is from another user, update it
        setTasks(prevTasks => prevTasks.map(task => 
          task.id === updatedTask.id ? updatedTask : task
        ));
        return prev;
      });
    });
    
    newSocket.on('task-deleted', (taskId: number) => {
      // Check if this was a local deletion
      setLocalActionIds(prev => {
        if (prev.has(taskId)) {
          // This was our own action, remove from tracking
          const newSet = new Set(prev);
          newSet.delete(taskId);
          return newSet;
        }
        // This is from another user, remove it
        setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        return prev;
      });
    });
    
    newSocket.on('disconnect', () => {
      console.log('Disconnected from socket server');
    });
    
    // Cleanup on unmount
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
      // Clean up preview URLs
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []); // Remove enqueueSnackbar from deps to avoid re-connections

  // Initial load (views filter the single fetched set client-side).
  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter tasks by search + platform + status (shared by both views).
  const filteredTasks = useMemo((): Task[] => {
    return tasks.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (task.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPlatform = filterPlatform === 'all' || task.platform === filterPlatform;
      const matchesStatus = filterStatus === 'all' || task.status === filterStatus;

      return matchesSearch && matchesPlatform && matchesStatus;
    });
  }, [tasks, searchTerm, filterPlatform, filterStatus]);

  // 我的任務 list: only tasks assigned to the current user. Pending first, sorted
  // by due date (nulls last) then priority; completed collapsed at the bottom
  // (last 7 days only).
  const myTasks = useMemo((): { pending: Task[]; completed: Task[] } => {
    const uid = currentUser?.id;
    if (!uid) return { pending: [], completed: [] };
    // A task is "mine" if I'm ANY of its assignees, not just the primary one.
    const mine = filteredTasks.filter(t =>
      Array.isArray(t.assignee_ids) && t.assignee_ids.length > 0
        ? t.assignee_ids.includes(uid)
        : t.assignee_id === uid
    );

    const dueMs = (t: Task): number => {
      if (!t.due_date) return Number.POSITIVE_INFINITY; // nulls last
      const d = new Date(t.due_date);
      return isNaN(d.getTime()) ? Number.POSITIVE_INFINITY : d.getTime();
    };

    const pending = mine
      .filter(t => t.status !== 'completed')
      .sort((a, b) => {
        const dd = dueMs(a) - dueMs(b);
        if (dd !== 0) return dd;
        return (priorityRank[a.priority] ?? 3) - (priorityRank[b.priority] ?? 3);
      });

    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const completedTs = (t: Task): number => {
      const src = t.completed_at || t.updated_at;
      const d = src ? new Date(src) : null;
      return d && !isNaN(d.getTime()) ? d.getTime() : 0;
    };
    const completed = mine
      .filter(t => t.status === 'completed' && completedTs(t) >= sevenDaysAgo)
      .sort((a, b) => completedTs(b) - completedTs(a));

    return { pending, completed };
  }, [filteredTasks, currentUser]);

  // Board drag-and-drop: optimistic section move + PUT /:id/section, revert on failure.
  // `claim` pulls an unassigned task into 進行中 — the backend assigns it to the
  // caller and returns the full row (assignee_id/assignee_name), which we merge in.
  const handleMoveSection = async (
    task: Task,
    section: string,
    opts?: { claim?: boolean }
  ): Promise<void> => {
    const prevSection = task.board_section ?? 'todo';
    const wasUnassigned = task.assignee_id == null;
    setLocalActionIds(prev => new Set(prev).add(task.id));
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, board_section: section } : t)));
    try {
      const body: { board_section: string; claim?: boolean } = { board_section: section };
      if (opts?.claim) body.claim = true;
      const res = await api.put<Task>(`/api/tasks/${task.id}/section`, body);
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...res.data } : t)));
      // Snackbar depends on whether the backend actually claimed it for the caller.
      if (opts?.claim && wasUnassigned && res.data.assignee_id != null) {
        enqueueSnackbar(`已認領：${res.data.title}`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Error moving task section:', error);
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, board_section: prevSection } : t)));
      enqueueSnackbar('移動任務失敗', { variant: 'error' });
    }
  };

  // Drag OUT of 已完成 into an active column: reopen (status→pending) THEN move to
  // the target section, sequentially. Revert both status and section on failure.
  const handleReopenTo = async (task: Task, section: string): Promise<void> => {
    const prevStatus = task.status;
    const prevSection = task.board_section ?? 'todo';
    setLocalActionIds(prev => new Set(prev).add(task.id));
    setTasks(prev =>
      prev.map(t => (t.id === task.id ? { ...t, status: 'pending', board_section: section } : t))
    );
    try {
      await api.put<Task>(`/api/tasks/${task.id}/status`, { status: 'pending' });
      const res = await api.put<Task>(`/api/tasks/${task.id}/section`, { board_section: section });
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...res.data } : t)));
    } catch (error) {
      console.error('Error reopening task:', error);
      setTasks(prev =>
        prev.map(t =>
          t.id === task.id ? { ...t, status: prevStatus, board_section: prevSection } : t
        )
      );
      enqueueSnackbar('重新開啟任務失敗', { variant: 'error' });
    }
  };

  // Quick-add from the top of the 待辦 column — creates the task immediately with
  // sensible defaults. Mirrors handleSubmit's local-action tracking to dedupe the
  // socket echo.
  const handleQuickAdd = async (title: string): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      const fd = new FormData();
      fd.append('title', trimmed);
      // platform is optional — quick-add omits it so the card gets NULL.
      fd.append('priority', 'medium');
      fd.append('board_section', 'todo');
      fd.append('description', '');
      fd.append('tags', '[]');
      const res = await api.post<Task>(`/api/tasks`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Track the new id so the incoming socket 'task-created' is not added twice,
      // then insert it optimistically for immediate feedback.
      setLocalActionIds(prev => new Set(prev).add(res.data.id));
      setTasks(prev => (prev.some(t => t.id === res.data.id) ? prev : [res.data, ...prev]));
      enqueueSnackbar('任務已建立', { variant: 'success' });
    } catch (error) {
      console.error('Error quick-adding task:', error);
      enqueueSnackbar('建立任務失敗', { variant: 'error' });
    }
  };

  // Stats calculation
  const stats = useMemo((): TaskStats => {
    const total = tasks.length;
    const pending = tasks.filter(t => t.status === 'pending').length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const highPriority = tasks.filter(t => t.status === 'pending' && t.priority === 'high').length;
    
    return { total, pending, completed, highPriority };
  }, [tasks]);

  // --- Task Board v2 helpers ---
  // Parse a due_date string (DATE column, may serialize as 'YYYY-MM-DD' or ISO) into a local Date.
  const parseDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  // Format a Date as 'YYYY-MM-DD' in local time (avoids UTC day-shift from toISOString).
  const toISODate = (d: Date | null): string => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDueDate = (value?: string | null): string => {
    const d = parseDate(value);
    if (!d) return '';
    return d.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  };

  // Overdue = pending task whose due date is strictly before today (date-only comparison).
  const isOverdue = (task: Task): boolean => {
    if (!task.due_date || task.status === 'completed') return false;
    const due = parseDate(task.due_date);
    if (!due) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
  };

  // Open the Asana-style detail drawer for a task.
  const handleOpenDetail = (task: Task): void => {
    setDetailTask(task);
    setDetailOpen(true);
  };

  const handleCloseDetail = (): void => {
    setDetailOpen(false);
  };

  // The drawer saved a change — reflect it in the list and keep the drawer in sync.
  const handleTaskUpdated = (updated: Task): void => {
    setLocalActionIds(prev => new Set(prev).add(updated.id));
    setTasks(prev => prev.map(t => (t.id === updated.id ? { ...t, ...updated } : t)));
    setDetailTask(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  // Link chip: no deep-link/query-param convention exists on the order/customer pages,
  // so copy the id to the clipboard and (for orders) jump to the storefront order page to paste.
  const handleLinkClick = async (task: Task): Promise<void> => {
    if (!task.link_type || !task.link_id) return;
    try {
      await navigator.clipboard?.writeText(task.link_id);
    } catch {
      // clipboard may be unavailable (insecure context) — proceed without failing
    }
    if (task.link_type === 'order') {
      enqueueSnackbar(`已複製訂單編號 ${task.link_id}，於訂單頁貼上搜尋`, { variant: 'info' });
      navigate('/storefront-orders');
    } else {
      enqueueSnackbar(`已複製顧客編號 ${task.link_id}`, { variant: 'info' });
    }
  };

  // The rule string the current control state means ('' = 不重複).
  const composeRecurrence = (): string => composeRule(recurEveryUnit, recurEveryN);

  // `section` preselects the board_section when creating from a board column.
  const handleOpenDialog = (task: Task | null = null, section: string = 'todo'): void => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description,
        platform: task.platform ?? '', // may be null → 不指定
        assignees: Array.isArray(task.assignee_ids) && task.assignee_ids.length > 0
          ? task.assignee_ids
          : (task.assignee_id ? [task.assignee_id] : []),
        tags: task.tags || [],
        priority: task.priority || 'medium',
        due_date: parseDate(task.due_date),
        board_section: task.board_section || 'todo'
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        platform: '',
        assignees: [],
        tags: [],
        priority: 'medium',
        due_date: null,
        board_section: section
      });
    }
    // Clear file selections; seed the 重複 control from the card being edited.
    setSelectedFiles([]);
    setPreviewUrls([]);
    const rc = ruleToControl(task ? task.recurrence : null);
    setRecurEveryN(rc.n);
    setRecurEveryUnit(rc.unit);
    setDialogOpen(true);
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      platform: '',
      assignees: [],
      tags: [],
      priority: 'medium',
      due_date: null,
      board_section: 'todo'
    });
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviewUrls([]);
    setRecurEveryN(1);
    setRecurEveryUnit('');
  };

  const handleSubmit = async (): Promise<void> => {
    if (!formData.title) {
      enqueueSnackbar('請填寫必要欄位', { variant: 'warning' });
      return;
    }
    
    setSubmitting(true);
    try {
    
      const headers: any = {
        'Content-Type': 'multipart/form-data'
      };
      
      
      // Create FormData for file upload
      const formDataObj = new FormData();
      formDataObj.append('title', formData.title);
      formDataObj.append('description', formData.description);
      // platform is optional — append only when chosen (empty → backend stores NULL).
      if (formData.platform) formDataObj.append('platform', formData.platform);
      formDataObj.append('priority', formData.priority);
      formDataObj.append('tags', JSON.stringify(formData.tags));
      formDataObj.append('board_section', formData.board_section || 'todo');

      // Always send the list: its presence tells the backend the assignment was
      // provided, and an empty array is how 未指派 is expressed.
      formDataObj.append('assignee_ids', JSON.stringify(formData.assignees));

      // Optional due date (server column is DATE)
      if (formData.due_date) {
        formDataObj.append('due_date', toISODate(formData.due_date));
      }
      
      // Add images
      selectedFiles.forEach(file => {
        formDataObj.append('images', file);
      });
      
      // 重複 applies to create AND edit. Always appended when editing — an empty
      // string is how 不重複 clears an existing rule (omitting the field would
      // read as "unchanged" server-side).
      const recurrenceRule = composeRecurrence();
      if (editingTask) {
        formDataObj.append('recurrence', recurrenceRule);
      } else if (recurrenceRule) {
        formDataObj.append('recurrence', recurrenceRule);
      }

      if (editingTask) {
        await api.put<Task>(
          `/api/tasks/${editingTask.id}`,
          formDataObj,
          { headers }
        );
        // Don't update state here - let Socket.IO handle it
        enqueueSnackbar(
          recurrenceRule
            ? `任務已更新，${recurrenceLabel(recurrenceRule)}重複`
            : '任務已更新',
          { variant: 'success' }
        );
      } else {
        // Create new task
        await api.post<Task>(
        `/api/tasks`,
  formDataObj,
  { headers }
);
        // Don't add to state here - let Socket.IO handle it
        enqueueSnackbar(
          recurrenceRule
            ? `已建立週期任務（${recurrenceLabel(recurrenceRule)}）`
            : '任務已建立',
          { variant: 'success' }
        );
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving task:', error);
      enqueueSnackbar('儲存任務失敗', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Set or clear a card's 週期 rule — the drag in/out of the 週期 column. When
  // `section` is given (dragging OUT), the card also moves to that column in the
  // same gesture. Optimistic: the lane is derived from `recurrence`, so the card
  // must jump immediately or the drag feels dropped.
  const handleSetRecurrence = async (
    task: Task,
    rule: string,
    section?: string
  ): Promise<void> => {
    const prevRecurrence = task.recurrence ?? null;
    const prevSection = task.board_section ?? 'todo';
    setLocalActionIds(prev => new Set(prev).add(task.id));
    setTasks(prev =>
      prev.map(t =>
        t.id === task.id
          ? { ...t, recurrence: rule || null, board_section: section ?? t.board_section }
          : t
      )
    );
    try {
      // assignee_id must be echoed back: PUT /api/tasks/:id assigns it directly
      // (no COALESCE), so omitting it from a partial update unassigns the card.
      const body: Record<string, unknown> = {
        recurrence: rule,
        assignee_id: task.assignee_id ?? null,
      };
      if (section) body.board_section = section;
      const res = await api.put<Task>(`/api/tasks/${task.id}`, body);
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...res.data } : t)));
      enqueueSnackbar(
        rule ? `已設為週期任務（${recurrenceLabel(rule)}）` : '已取消週期',
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Error updating recurrence:', error);
      setTasks(prev =>
        prev.map(t =>
          t.id === task.id
            ? { ...t, recurrence: prevRecurrence, board_section: prevSection }
            : t
        )
      );
      enqueueSnackbar('更新週期失敗', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (task: Task): Promise<void> => {
    try {
      const newStatus = task.status === 'pending' ? 'completed' : 'pending';
     
      
      
      const response = await api.put<Task>(
        `/api/tasks/${task.id}/status`,
        { status: newStatus }
      );
      // Mark this as a local action to prevent duplicate from socket
      setLocalActionIds(prev => new Set(prev).add(response.data.id));
      setTasks(prev => prev.map(t =>
        t.id === task.id ? response.data : t
      ));
      // A 週期任務 doesn't close — the server reschedules it and reports the next
      // date. Say so, otherwise the card bouncing back to 待辦 reads as a bug.
      const nextDue = response.data.recurring_next_due;
      enqueueSnackbar(
        nextDue
          ? `已完成，下一次 ${formatDueDate(nextDue)}`
          : newStatus === 'completed' ? '任務已完成' : '任務已重新開啟',
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Error updating task status:', error);
      enqueueSnackbar('更新狀態失敗', { variant: 'error' });
    }
  };

  // Returns true only when the task was actually deleted (confirm accepted +
  // API success) — callers use it to decide whether to close the drawer.
  const handleDeleteTask = async (taskId: number): Promise<boolean> => {
    if (!window.confirm('確定要刪除這個任務嗎？')) return false;

    try {
      await api.delete(`/api/tasks/${taskId}`);
      // Mark this as a local action to prevent duplicate from socket
      setLocalActionIds(prev => new Set(prev).add(taskId));
      setTasks(prev => prev.filter(task => task.id !== taskId));
      enqueueSnackbar('任務已刪除', { variant: 'success' });
      return true;
    } catch (error) {
      console.error('Error deleting task:', error);
      enqueueSnackbar('刪除任務失敗', { variant: 'error' });
      return false;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTagIcon = (tag: string): React.ReactElement => {
    const iconMap: Record<string, React.ReactElement> = {
      '補寄': <ShippingIcon fontSize="small" />,
      '聯繫': <PhoneIcon fontSize="small" />,
      '取消訂單': <CancelIcon fontSize="small" />,
      '問物流': <ShippingIcon fontSize="small" />,
    };
    return iconMap[tag] || <LabelIcon fontSize="small" />;
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom>
              任務追蹤管理
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理團隊任務，追蹤處理進度
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Card sx={{ minWidth: 100, bgcolor: 'primary.light', color: 'white' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="h6">{stats.total}</Typography>
                  <Typography variant="caption">總任務</Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 100, bgcolor: 'warning.main', color: 'white' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="h6">{stats.pending}</Typography>
                  <Typography variant="caption">進行中</Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 100, bgcolor: 'success.main', color: 'white' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="h6">{stats.completed}</Typography>
                  <Typography variant="caption">已完成</Typography>
                </CardContent>
              </Card>
              {stats.highPriority > 0 && (
                <Card sx={{ minWidth: 100, bgcolor: 'error.main', color: 'white' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Badge badgeContent="!" color="warning">
                      <Box>
                        <Typography variant="h6">{stats.highPriority}</Typography>
                        <Typography variant="caption">高優先</Typography>
                      </Box>
                    </Badge>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Main views: Asana-style board | my assigned tasks */}
      <Paper sx={{ px: 2, mb: 3 }}>
        <Tabs
          value={mainView}
          onChange={(_, newValue: MainView) => setMainView(newValue)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="看板" value="board" />
          <Tab label="我的任務" value="my" />
        </Tabs>
      </Paper>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="搜尋任務..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>平台</InputLabel>
              <Select
                value={filterPlatform}
                label="平台"
                onChange={(e: SelectChangeEvent) => setFilterPlatform(e.target.value)}
              >
                <MenuItem value="all">全部</MenuItem>
                {PLATFORM_OPTIONS.map(platform => (
                  <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>狀態</InputLabel>
              <Select
                value={filterStatus}
                label="狀態"
                onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="pending">進行中</MenuItem>
                <MenuItem value="completed">已完成</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            {/* No 週期任務 manager button: recurring cards live on the board like
                any other card, so there is no separate list to manage. */}
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              <IconButton onClick={fetchTasks} color="primary">
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Tasks Display */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : mainView === 'board' ? (
        <TaskBoardView
          tasks={filteredTasks}
          onOpenDetail={handleOpenDetail}
          onToggleStatus={handleToggleStatus}
          onMoveSection={handleMoveSection}
          onReopenTo={handleReopenTo}
          onCreateInSection={(section) => handleOpenDialog(null, section)}
          onQuickAdd={handleQuickAdd}
          onDelete={(task) => handleDeleteTask(task.id)}
          onSetRecurrence={handleSetRecurrence}
          employees={employees}
        />
      ) : (
        /* 我的任務 — only tasks assigned to the current user, Asana-like rows */
        <Paper>
          {myTasks.pending.length === 0 && myTasks.completed.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography variant="h6" color="text.secondary">
                目前沒有指派給你的任務
              </Typography>
            </Box>
          ) : (
            <Box>
              {myTasks.pending.map(task => (
                <Box
                  key={task.id}
                  onClick={() => handleOpenDetail(task)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, cursor: 'pointer',
                    borderBottom: '1px solid', borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Checkbox
                    size="small"
                    color="success"
                    checked={false}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggleStatus(task)}
                  />
                  <Typography flex={1} noWrap>{task.title}</Typography>
                  {task.due_date && (
                    <Chip
                      icon={<EventIcon />}
                      label={formatDueDate(task.due_date)}
                      size="small"
                      color={isOverdue(task) ? 'error' : 'default'}
                      variant={isOverdue(task) ? 'filled' : 'outlined'}
                    />
                  )}
                  <Chip
                    label={priorityLabels[task.priority]}
                    size="small"
                    color={priorityColors[task.priority]}
                  />
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(task); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {myTasks.completed.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pt: 2, pb: 0.5 }}>
                    已完成（近 7 天）
                  </Typography>
                  {myTasks.completed.map(task => (
                    <Box
                      key={task.id}
                      onClick={() => handleOpenDetail(task)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 1, cursor: 'pointer', opacity: 0.6,
                        borderBottom: '1px solid', borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Checkbox
                        size="small"
                        color="success"
                        checked
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggleStatus(task)}
                      />
                      <Typography flex={1} noWrap sx={{ textDecoration: 'line-through' }}>{task.title}</Typography>
                      {task.due_date && (
                        <Chip icon={<EventIcon />} label={formatDueDate(task.due_date)} size="small" variant="outlined" />
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog()}
      >
        <AddIcon />
      </Fab>

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? '編輯任務' : '新增任務'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="任務標題"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="任務描述"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>平台</InputLabel>
              <Select
                value={formData.platform}
                label="平台"
                onChange={(e: SelectChangeEvent) => setFormData({ ...formData, platform: e.target.value })}
              >
                {/* Platform is optional — 不指定 leaves it NULL. */}
                <MenuItem value="">不指定</MenuItem>
                {PLATFORM_OPTIONS.map(platform => (
                  <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* 指派給 — multiple people per task; empty = 未指派. */}
            <FormControl fullWidth>
              <InputLabel>指派給</InputLabel>
              <Select
                multiple
                value={formData.assignees}
                label="指派給"
                onChange={(e: SelectChangeEvent<number[]>) => {
                  const raw = e.target.value;
                  const ids = (typeof raw === 'string' ? raw.split(',').map(Number) : raw)
                    .filter((n) => !Number.isNaN(n));
                  setFormData({ ...formData, assignees: ids });
                }}
                renderValue={(selected) =>
                  (selected as number[]).length === 0
                    ? '未指派'
                    : (selected as number[])
                        .map((id) => employees.find((e) => e.id === id)?.name ?? `#${id}`)
                        .join('、')
                }
              >
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    <Checkbox size="small" checked={formData.assignees.includes(emp.id)} />
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>優先級</InputLabel>
              <Select
                value={formData.priority}
                label="優先級"
                onChange={(e: SelectChangeEvent) => 
                  setFormData({ ...formData, priority: e.target.value as 'low' | 'medium' | 'high' })
                }
              >
                {priorities.map(priority => (
                  <MenuItem key={priority} value={priority}>
                    <Chip
                      label={priorityLabels[priority]}
                      size="small"
                      color={priorityColors[priority]}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="截止日期"
                value={formData.due_date}
                onChange={(v: Date | null) => setFormData({ ...formData, due_date: v })}
                slotProps={{
                  textField: { fullWidth: true },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>

            {/* 重複 — a number and a unit, any interval. Editable on an existing
                card too, so a task that turns out to be a routine can be marked
                週期 in place. */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 32 }}>重複</Typography>
                <TextField
                  label="每"
                  type="number"
                  size="small"
                  value={recurEveryN}
                  disabled={recurEveryUnit === ''}
                  onChange={(e) => setRecurEveryN(clampInterval(e.target.value, recurEveryUnit))}
                  inputProps={{ min: 1, max: recurEveryUnit === 'w' ? 52 : 365 }}
                  sx={{ width: 100 }}
                />
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>單位</InputLabel>
                  <Select
                    value={recurEveryUnit}
                    label="單位"
                    onChange={(e: SelectChangeEvent) => {
                      const unit = e.target.value as RecurUnit;
                      setRecurEveryUnit(unit);
                      setRecurEveryN((n) => clampInterval(n, unit));
                    }}
                  >
                    {RECUR_UNITS.map((u) => (
                      <MenuItem key={u.value || 'none'} value={u.value}>
                        {u.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {composeRecurrence() && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  放在「週期」欄提醒，{recurrenceLabel(composeRecurrence())}提醒一次
                </Typography>
              )}
            </Box>
            <Autocomplete
              multiple
              options={commonTags}
              value={formData.tags}
              onChange={(_, newValue: string[]) => setFormData({ ...formData, tags: newValue })}
              freeSolo
              renderInput={(params) => (
                <TextField {...params} label="標籤" placeholder="選擇或輸入標籤" />
              )}
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    icon={getTagIcon(option)}
                    key={index}
                  />
                ))
              }
            />

            {/* Image Upload Section */}
<Box>
  <Typography variant="subtitle1" gutterBottom>
    圖片上傳 (最多 5 張)
  </Typography>
  <input
    ref={fileInputRef}
    type="file"
    multiple
    accept="image/*"
    style={{ display: 'none' }}
    onChange={(e) => handleFileSelect(e.target.files)}
  />
  <Stack direction="row" spacing={1} mb={2}>
    <Button
      variant="outlined"
      startIcon={<CloudUploadIcon />}
      onClick={() => fileInputRef.current?.click()}
      disabled={selectedFiles.length >= 5}
    >
      選擇圖片
    </Button>
    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
      已選擇 {selectedFiles.length}/5 張圖片
    </Typography>
  </Stack>
  
  {/* Preview selected files */}
  {previewUrls.length > 0 && (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        預覽
      </Typography>
      <ImageList cols={3} rowHeight={100} sx={{ maxHeight: 200 }}>
        {previewUrls.map((url, index) => (
          <ImageListItem key={index}>
            <img
              src={url}
              alt={`Preview ${index + 1}`}
              loading="lazy"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover' 
              }}
            />
            <ImageListItemBar
              sx={{ 
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
              }}
              position="top"
              actionIcon={
                <IconButton
                  sx={{ color: 'white' }}
                  size="small"
                  onClick={() => removeSelectedFile(index)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            />
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  )}
  </Box>
  
            {/* Show existing images for editing */}
{editingTask && editingTask.images && editingTask.images.length > 0 && (
  <Box mt={2}>
    <Typography variant="subtitle2" gutterBottom>
      現有圖片
    </Typography>
    <ImageList cols={3} rowHeight={100} sx={{ maxHeight: 200 }}>
      {editingTask.images.map((base64Image, index) => (
        <ImageListItem key={index}>
          <img
            src={base64Image} // Direct Base64 string
            alt={`Existing image ${index + 1}`}
            loading="lazy"
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover' 
            }}
          />
          <ImageListItemBar
            sx={{ 
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
            }}
            position="top"
            actionIcon={
              <IconButton
                sx={{ color: 'white' }}
                size="small"
                onClick={() => removeTaskImage(editingTask.id, index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            }
          />
        </ImageListItem>
      ))}
    </ImageList>
  </Box>
)}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : (editingTask ? '更新' : '建立')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog 
        open={imageViewerOpen} 
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">圖片檢視</Typography>
            <IconButton onClick={() => setImageViewerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box textAlign="center">
              <img
                src={selectedImage}
                alt="Task image"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh', 
                  objectFit: 'contain' 
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
  startIcon={<DownloadIcon />}
  onClick={() => {
    // For Base64 images, create a download link
    const link = document.createElement('a');
    link.href = selectedImage;
    link.download = `task-image-${Date.now()}.png`;
    link.click();
  }}
>
  下載
</Button>
          <Button onClick={() => setImageViewerOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>任務歷史紀錄</DialogTitle>
        <DialogContent>
          {selectedTaskHistory && selectedTaskHistory.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {selectedTaskHistory.map((history, index) => (
                <Paper key={index} sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      <strong>{history.user_name}</strong> {history.action}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(history.created_at)}
                    </Typography>
                  </Stack>
                  {history.changes && (
                    <Typography variant="caption" color="text.secondary">
                      {JSON.stringify(history.changes)}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography>暫無歷史紀錄</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* Asana-style task detail drawer */}
      <TaskDetailDrawer
        open={detailOpen}
        task={detailTask}
        employees={employees}
        onClose={handleCloseDetail}
        onTaskUpdated={handleTaskUpdated}
        onDelete={async (task) => {
          const deleted = await handleDeleteTask(task.id);
          if (deleted) handleCloseDetail();
        }}
      />

    </Box>
  );
};

export default TaskTrackingPage;